/**
 * Activity Tracker — core tracking engine for OpSyncPro.
 * Manages sessions, heartbeats, idle detection, event buffering, and batch flushing.
 * Designed to be lightweight and never block user interactions.
 */

import { logError } from './betterstack-logger';
import { captureHandled, isBrowserExtensionUrl } from './sentry-capture';

const FLUSH_INTERVAL_MS = 15000;
const FLUSH_BATCH_SIZE = 25;
const MAX_BUFFER_SIZE = 500;
const HEARTBEAT_INTERVAL_MS = 60000;
const IDLE_THRESHOLD_MS = 180000; // 3 minutes
const IDLE_CHECK_INTERVAL_MS = 10000;

let buffer = [];
let sessionId = null;
let getAuthToken = null;
let isInitialized = false;
let flushTimer = null;
let heartbeatTimer = null;
let idleCheckTimer = null;
let lastActivityTimestamp = Date.now();
let lastHeartbeatTimestamp = Date.now();
let activeSecondsSinceLastHeartbeat = 0;
let isIdle = false;
let isPageVisible = true;
let activityListenersAttached = false;

function getOrCreateSessionId() {
  if (sessionId) return sessionId;
  const stored = sessionStorage.getItem('activitySessionId');
  if (stored) {
    sessionId = stored;
    return sessionId;
  }
  sessionId = crypto.randomUUID();
  sessionStorage.setItem('activitySessionId', sessionId);
  return sessionId;
}

function recordActivity() {
  const now = Date.now();
  const elapsed = Math.min((now - lastActivityTimestamp) / 1000, 5);
  if (!isIdle) {
    activeSecondsSinceLastHeartbeat += elapsed;
  }
  lastActivityTimestamp = now;

  if (isIdle) {
    const idleDuration = Math.round((now - lastActivityTimestamp) / 1000);
    isIdle = false;
    trackEvent('idle_end', null, { idle_duration_seconds: idleDuration });
  }
}

function attachActivityListeners() {
  if (activityListenersAttached) return;
  const handler = () => recordActivity();
  document.addEventListener('mousemove', handler, { passive: true });
  document.addEventListener('keydown', handler, { passive: true });
  document.addEventListener('scroll', handler, { passive: true });
  document.addEventListener('click', handler, { passive: true });
  document.addEventListener('touchstart', handler, { passive: true });
  activityListenersAttached = true;
}

function startIdleDetection() {
  idleCheckTimer = setInterval(() => {
    if (isIdle) return;
    const elapsed = Date.now() - lastActivityTimestamp;
    if (elapsed > IDLE_THRESHOLD_MS) {
      isIdle = true;
      trackEvent('idle_start', null, { last_activity_seconds_ago: Math.round(elapsed / 1000) });
    }
  }, IDLE_CHECK_INTERVAL_MS);
}

function startHeartbeat() {
  heartbeatTimer = setInterval(() => {
    if (!isPageVisible) return;
    const activeSeconds = Math.round(Math.min(activeSecondsSinceLastHeartbeat, 60));
    trackEvent('heartbeat', null, { active_seconds_since_last: activeSeconds });
    activeSecondsSinceLastHeartbeat = 0;
    lastHeartbeatTimestamp = Date.now();
  }, HEARTBEAT_INTERVAL_MS);
}

function startVisibilityTracking() {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      isPageVisible = false;
      trackEvent('focus_lost', null, {});
    } else {
      isPageVisible = true;
      trackEvent('focus_gained', null, {});
      lastActivityTimestamp = Date.now();
    }
  });
}

// --- Public API ---

export function initTracker(tokenGetter) {
  if (isInitialized) return;
  // Only track in production — skip dev and automated test environments
  if (import.meta.env.DEV || navigator.webdriver) {
    console.log('[activity-tracker] Skipping init in dev/test mode');
    isInitialized = true;
    return;
  }

  getAuthToken = tokenGetter;
  getOrCreateSessionId();
  attachActivityListeners();
  startIdleDetection();
  startHeartbeat();
  startVisibilityTracking();
  startFlushTimer();

  // Session start event
  trackEvent('session_start', null, {
    user_agent: navigator.userAgent,
    screen_size: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Session end on page unload
  window.addEventListener('beforeunload', () => {
    const duration = Math.round((Date.now() - (performance.timeOrigin || Date.now())) / 1000);
    bufferEvent({
      table: 'user_events',
      session_id: sessionId,
      event_type: 'session_end',
      event_target: null,
      metadata: { duration_seconds: duration, page_count: 0 },
      created_at: new Date().toISOString(),
    });
    flushBeacon();
  });

  // Supplementary error handlers
  window.addEventListener('error', (event) => {
    // Skip errors thrown by a third-party browser extension (OPS-1017 / OPS-1021).
    // Three pipelines see this same window 'error' event — Sentry (via `denyUrls`),
    // the error-report relay, and this tracker — and `denyUrls` reaches only Sentry,
    // so each other consumer guards itself. See `isBrowserExtensionUrl`.
    //
    // Unguarded, this is the costliest of the three: it has no dedupe at all, and
    // `bufferEvent` below evicts the oldest entry once the buffer is full. So an
    // extension erroring in a loop does not merely add noise — it DESTROYS real
    // telemetry before it is ever flushed, and crowds genuine errors out of the
    // admin dashboard's bounded error_summary window.
    //
    // The `unhandledrejection` handler below is deliberately left unguarded: a
    // PromiseRejectionEvent has no `filename` to match on, and Sentry still covers
    // it via frames synthesized from `reason.stack`.
    if (isBrowserExtensionUrl(event.filename)) return;
    trackMutation('frontend_error', 'error', null, null,
      event.message || 'Unknown error',
      { stack: event.error?.stack?.substring(0, 1000), page: window.location.pathname },
      'error'
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    trackMutation('frontend_error', 'error', null, null,
      err.message,
      { stack: err.stack?.substring(0, 1000), page: window.location.pathname },
      'error'
    );
  });

  isInitialized = true;
}

export function trackEvent(eventType, eventTarget, metadata = {}) {
  if (!isInitialized || import.meta.env.DEV) return;
  bufferEvent({
    table: 'user_events',
    session_id: getOrCreateSessionId(),
    event_type: eventType,
    event_target: eventTarget,
    metadata,
    created_at: new Date().toISOString(),
  });
}

export function trackMutation(action, category, entityType, entityId, description, metadata = {}, severity = 'info') {
  if (!isInitialized || import.meta.env.DEV) return;
  bufferEvent({
    table: 'activity_logs',
    session_id: getOrCreateSessionId(),
    action,
    category: category || 'mutation',
    entity_type: entityType,
    entity_id: entityId,
    description,
    metadata,
    severity,
    source: 'frontend',
    created_at: new Date().toISOString(),
  });
}

export function trackApiError(url, method, status) {
  if (!isInitialized || import.meta.env.DEV) return;
  trackMutation(
    'api_error', 'error', null, null,
    `${method || 'GET'} ${url} -> ${status}`,
    { status, url, method: method || 'GET' },
    status >= 500 ? 'error' : 'warning'
  );
}

export function destroyTracker() {
  clearInterval(flushTimer);
  clearInterval(heartbeatTimer);
  clearInterval(idleCheckTimer);
  flushBeacon();
  isInitialized = false;
}

// --- Internal ---

function bufferEvent(event) {
  if (buffer.length >= MAX_BUFFER_SIZE) {
    buffer.shift(); // Drop oldest to prevent memory leak
  }
  buffer.push(event);

  if (buffer.length >= FLUSH_BATCH_SIZE) {
    flush();
  }
}

function startFlushTimer() {
  flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
}

async function flush() {
  if (buffer.length === 0 || !getAuthToken) return;

  const batch = buffer.splice(0);

  try {
    const token = await getAuthToken();
    const response = await fetch('/.netlify/functions/track-events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });

    if (!response.ok) {
      logError('activity-flush-failed', { status: response.status, batchSize: batch.length });
      // Route to Sentry — the only pipeline that tickets (OPS-513). The stable
      // 'activity-flush-failed' fingerprint collapses every retry (15s timer +
      // each beacon) of a sustained outage into ONE Better Stack group → one
      // card, so no per-session throttle is needed. The status is a tag-style
      // value in the extra, NOT in the event key, so a 502/503 don't fan out.
      captureHandled(
        new Error(`activity-flush-failed: ${response.status}`),
        'activity-flush-failed',
        { status: response.status, batchSize: batch.length }
      );
      // Push events back on failure (if room)
      if (buffer.length + batch.length <= MAX_BUFFER_SIZE) {
        buffer.unshift(...batch);
      }
    }
  } catch (err) {
    logError('activity-flush-error', { error: err?.message, batchSize: batch.length });
    // Same fingerprint-collapses-repeats rationale as the !response.ok branch.
    captureHandled(
      err instanceof Error ? err : new Error('activity-flush-error'),
      'activity-flush-error',
      { batchSize: batch.length }
    );
    if (buffer.length + batch.length <= MAX_BUFFER_SIZE) {
      buffer.unshift(...batch);
    }
  }
}

function flushBeacon() {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0);
  // sendBeacon can't set headers, embed token in payload
  let token = null;
  try {
    // Synchronously read from sessionStorage as a last resort
    // getAuthToken is async and can't be used in beforeunload
    const sb = sessionStorage.getItem('sb-auth-token');
    if (sb) token = sb;
  } catch { /* ignore */ }

  const payload = JSON.stringify({ events: batch, token });
  navigator.sendBeacon('/.netlify/functions/track-events', new Blob([payload], { type: 'application/json' }));
}
